/**
 * Column definitions shared by NewTaskGrid and its loading skeleton so the two
 * always render the same columns in the same widths.
 *
 * className must stay in sync with the width rules in NewTaskGrid.scss.
 * skeletonWidth is relative on purpose: the table is `table-layout: fixed`, so a
 * pixel placeholder wider than its column overflows into the next cell.
 *
 * sortKey is the column name the task list API sorts on. Columns without one
 * cannot be sorted server side (the API exposes no assignee or actual-hours
 * sort), so their headers render as plain labels.
 *
 * Optional columns:
 * - requiresActualTime — shown when getTaskGridColumns(..., isShowActualTime)
 * - requiresRemainingTime — shown when getTaskGridColumns(..., isShowRemainingTime);
 *   editable inline in NewTaskGrid when selectionMode is "planning" (assigned hours for Save)
 */
const TASK_GRID_COLUMNS = [
  { key: "checkbox", className: "taskgrid-checkbox-column", label: "" },
  {
    key: "type",
    className: "taskgrid-type-column",
    label: "Task Type/ID",
    skeletonWidth: "70%",
    sortKey: "TaskId",
    sortLabel: "Task ID",
  },
  {
    key: "title",
    className: "taskgrid-title-column",
    label: "Task Name",
    skeletonWidth: "85%",
    sortKey: "Title",
    sortLabel: "Title",
  },
  {
    key: "estimate",
    className: "taskgrid-est-column",
    label: "Est. Time",
    skeletonWidth: "55%",
    sortKey: "EstimateHours",
    sortLabel: "Est. hours",
  },
  {
    key: "remaining",
    className: "taskgrid-rem-column",
    label: "Rem. Time",
    skeletonWidth: "55%",
    requiresRemainingTime: true,
  },
  {
    key: "actual",
    className: "taskgrid-actual-column",
    label: "Actual Time",
    skeletonWidth: "55%",
    requiresActualTime: true,
  },
  {
    key: "priority",
    className: "taskgrid-priority-column",
    label: "Priority",
    skeletonWidth: "70%",
    sortKey: "PriorityId",
    sortLabel: "Priority",
  },
  {
    key: "deadline",
    className: "taskgrid-deadline-column",
    label: "Deadline",
    skeletonWidth: "75%",
    sortKey: "DeadLine",
    sortLabel: "Deadline",
  },
  {
    key: "status",
    className: "taskgrid-status-column",
    label: "Status",
    skeletonWidth: "80%",
    sortKey: "TaskStatusId",
    sortLabel: "Status",
  },
  { key: "assignee", className: "taskgrid-assignee-avatar-column", label: "Assignee", skeletonWidth: "25px" },
  { key: "action", className: "taskgrid-action-column", label: "Action", skeletonWidth: "80px" },
];

export const TASK_SORT_ORDERS = {
  ASC: { label: "Ascending", value: "ASC" },
  DESC: { label: "Descending", value: "DESC" },
};

export const TASK_SORT_ORDER_OPTIONS = [TASK_SORT_ORDERS.ASC, TASK_SORT_ORDERS.DESC];

// The Sort dropdown and the sortable column headers write to the same context,
// so both pick their options from here to keep labels and keys identical.
export const TASK_SORT_OPTIONS = TASK_GRID_COLUMNS
  .filter((column) => column.sortKey)
  .map((column) => ({ label: column.sortLabel, value: column.sortKey }));

export const getTaskGridColumns = (isShowActualTime = false, isShowRemainingTime = false) =>
  TASK_GRID_COLUMNS.filter((column) => {
    if (column.requiresActualTime && !isShowActualTime) return false;
    if (column.requiresRemainingTime && !isShowRemainingTime) return false;
    return true;
  });

export default TASK_GRID_COLUMNS;
